import { Home, BarChart2, Settings, Bot, PanelLeftClose, PanelLeft } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sidebar */}
      <motion.div 
        initial={false}
        animate={{ width: isCollapsed ? "64px" : "256px" }}
        className={cn(
          "fixed left-0 top-0 h-full bg-white border-r border-gray-200 p-4 flex flex-col",
          "transition-all duration-300 ease-in-out"
        )}
      >
        <div className={cn(
          "mb-8 px-4",
          isCollapsed && "opacity-0 h-0 mb-0 overflow-hidden"
        )}>
          <img
            src="/lovable-uploads/99af36c9-7743-4308-adc1-ee20870b7654.png"
            alt="Hypersight Logo"
            className="w-32"
          />
        </div>
        <nav className="space-y-2">
          <Link
            to="/dashboard"
            className={cn(
              "flex items-center gap-3 px-4 py-2 rounded-lg transition-colors",
              isActive('/dashboard') 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            <Home className="w-5 h-5" />
            {!isCollapsed && <span>Home</span>}
          </Link>
          <Link
            to="/reports"
            className={cn(
              "flex items-center gap-3 px-4 py-2 rounded-lg transition-colors",
              isActive('/reports') 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            <BarChart2 className="w-5 h-5" />
            {!isCollapsed && <span>Reports</span>}
          </Link>
          <Link
            to="/hyperagent"
            className={cn(
              "flex items-center gap-3 px-4 py-2 rounded-lg transition-colors",
              isActive('/hyperagent') 
                ? 'bg-primary text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            <Bot className="w-5 h-5" />
            {!isCollapsed && <span>Hyperagent</span>}
          </Link>
          <div
            className={cn(
              "flex items-center gap-3 px-4 py-2 rounded-lg text-gray-400 cursor-not-allowed",
            )}
          >
            <Settings className="w-5 h-5" />
            {!isCollapsed && <span>Settings</span>}
          </div>
        </nav>
        
        {/* Back to Website Button */}
        <div className="mt-auto pt-4">
          {!isCollapsed && (
            <Button
              variant="outline"
              className="w-full mb-4"
              onClick={() => window.location.href = 'https://hypersight.se'}
            >
              Back to Website
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full flex items-center justify-center"
          >
            {isCollapsed ? (
              <PanelLeft className="w-4 h-4" />
            ) : (
              <PanelLeftClose className="w-4 h-4" />
            )}
          </Button>
        </div>
      </motion.div>

      {/* Main Content */}
      <div 
        className={cn(
          "transition-all duration-300 ease-in-out",
          isCollapsed ? "pl-16" : "pl-64"
        )}
      >
        <main className="p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;